import React, { useEffect, useState } from "react";
import { Select, Button } from "antd";
import api from '../../../services/api/';
import { PiPencilDuotone } from "react-icons/pi";
const { Option } = Select;
import { useAuthSettingUtils } from 'helpers/settings/settingUtils';

const CountrySelect = ({
    selectedCountry,
    selectedState,
    onChange,
    disableCountry = false,
}) => {
    const { getAuthSetting, getAuthOrganization } = useAuthSettingUtils();
    const [selectedCountryId, setSelectedCountryId] = useState(null);
    const [selectedStateId, setSelectedStateId] = useState(null);

    const getDefaultLocationIds = () => {
        const organization = getAuthOrganization?.() || {};

        return {
            country_id: parseInt(organization?.country?.id || getAuthSetting('country_id')) || null,
            state_id: parseInt(organization?.state?.id || getAuthSetting('state_id')) || null,
        };
    };

    useEffect(() => {
        const defaultLocation = getDefaultLocationIds();
        const nextCountryId = parseInt(selectedCountry, 10) || defaultLocation.country_id;
        const nextStateId = parseInt(selectedState, 10) || defaultLocation.state_id;

        setSelectedCountryId(nextCountryId);
        setSelectedStateId(nextStateId);
    }, [selectedCountry, selectedState]);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState({ country: false, state: false });
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchCountries();
    }, []);

    useEffect(() => {
        if (selectedCountryId) {
            fetchStates(selectedCountryId);
        } else {
            setStates([]); // Clear states when no country is selected
        }
    }, [selectedCountryId]);

    const fetchCountries = async () => {
        setLoading(prev => ({ ...prev, country: true }));
        try {
            const response = await api.get("/countries");
            setCountries(response.data.data);
        } finally {
            setLoading(prev => ({ ...prev, country: false }));
        }
    };

    const fetchStates = async (countryId) => {
        setLoading(prev => ({ ...prev, state: true }));
        try {
            const response = await api.get(`/states/${countryId}`);
            setStates(response.data.data);
        } finally {
            setLoading(prev => ({ ...prev, state: false }));
        }
    };

    const getLocationName = (id, array) => {
        const item = array.find(item => item.id === id);
        return item ? item.name : '';
    };

    const displayAddress = () => {
        const countryName = getLocationName(selectedCountryId, countries);
        const stateName = getLocationName(selectedStateId, states);

        return (
            <div >
                {countryName && <span>{countryName} </span>}
                {stateName && <span>{stateName} </span>}
                <Button size="small" type="link" onClick={() => setIsEditing(true)} icon={<PiPencilDuotone />}>Edit</Button>
                {disableCountry && <span className="text-muted ms-2" style={{ fontSize: '11px' }}>(Country locked)</span>}
            </div>
        );
    };

    const displayForm = () => {
        return (
            <div>
                {/* Country Dropdown */}
                <Select
                    showSearch
                    placeholder="Select Country"
                    value={selectedCountryId}
                    onChange={value => {
                        setSelectedCountryId(value);
                        setSelectedStateId(null); // Reset state when country changes
                        onChange({ country_id: value, state_id: null });
                    }}
                    onClear={() => {
                        setSelectedCountryId(null);
                        setSelectedStateId(null);
                        onChange({ country_id: null, state_id: null });
                    }}
                    style={{ width: "100%", marginBottom: 10 }}
                    loading={loading.country}
                    disabled={disableCountry}
                    allowClear={!disableCountry}
                    filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {countries.map(country => (
                        <Option key={country.id} value={country.id}>{country.name}</Option>
                    ))}
                </Select>
                {disableCountry && (
                    <div className="mb-2" style={{ fontSize: '12px', color: 'var(--ant-color-text-description)' }}>
                        <span>ℹ️ Country selection is restricted. You can change State only.</span>
                    </div>
                )}

                {/* State Dropdown */}
                <Select
                    showSearch
                    placeholder="Select State"
                    value={selectedStateId}
                    onChange={value => {
                        setSelectedStateId(value);
                        onChange({ country_id: selectedCountryId, state_id: value });
                    }}
                    onClear={() => {
                        setSelectedStateId(null);
                        onChange({ country_id: selectedCountryId, state_id: null });
                    }}
                    style={{ width: "100%", marginBottom: 10 }}
                    loading={loading.state}
                    disabled={!selectedCountryId}
                    allowClear
                    filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {states.map(state => (
                        <Option key={state.id} value={state.id}>{state.name}</Option>
                    ))}
                </Select>

                <Button type="primary" size="small" onClick={() => setIsEditing(false)}>Done</Button>
            </div>
        );
    };

    return (
        <div>
            {isEditing ? displayForm() : displayAddress()}
        </div>
    );
};

export default CountrySelect;
